import { useEffect, useMemo, useState, useRef, useCallback } from "react";
import clsx from "clsx";
import {
  navHide,
  callbackData,
  onWebviewClose,
  preloadImages,
} from "../../../../utils/common";
import UserHeader from "../../components/user-header";
import { entry, getGoodsType, getReportVipData } from "../../utils";
import CouponRedemptionComponent from "../../components/coupon-redemption";
import BookPile from "../../components/bookPile";

import "./index.less";

import TabGoodsContent from "../../components/tab-goods-content";
import { GoodsFocusProvider } from "../../components/tab-goods-content/GoodsFocusContext";
import Page from "@/pages/par-marketing/configPage/page";
import {
  ConfigPageProvider,
  useConfigPage,
} from "@/hooks/useConfigPage/useConfigPage";
import { getQueryHashString, removeUrlParameters } from "@/utils/url";
import useFetchConfigPageForVip from "@/hooks/useConfigPage/useFetchConfigPageForVip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useEventListener, useMount, useThrottleFn } from "ahooks";
import { UserProviderWithConfigPage, useUserContext } from "./userContext";
import { trackGdp } from "@/utils/gio-report";
import { processOssImage } from "@/utils/image";
import FullScreenPopup from "../../components/full-screen-popup";
import * as Sentry from "@sentry/react";
import LazyRender from "@/components/LazyRender";
import SkeletonScreen from "./SkeletonScreen";
import AppNavBar from "../../components/app-nav-bar";
import OpenBar from "../../components/open-bar";
import { getBrowserEnv, rem2px } from "@/utils/tools";
const { isXPApp, isWXApp, isMyApp, isWX } = getBrowserEnv();

// 背景图预加载
preloadImages([
  processOssImage(
    "https://s.xiaopeng.com/xp-charge/charge/charge-vip/vip-header-bg-v2.png",
  ),
]);

const ConfigPage = () => {
  const { configPageQuery } = useConfigPage();
  const { levelInfo, userInfo, goodsData, isLoading, userType } =
    useUserContext();
  const [showDiscountPopup, setShowDiscountPopup] = useState(false);
  const [showVipUpPopup, setShowVipUpPopup] = useState(false);
  const [discountCouponImg, setDiscountCouponImg] = useState<string>("");
  const [navBgWhite, setNavBgWhite] = useState(false);
  const [hasStickyBuyContent, setHasStickyBuyContent] = useState(false);
  const userHeaderRef = useRef<HTMLDivElement>(null);

  const vipPopupImgUrl = useMemo(() => {
    if (!levelInfo?.level || levelInfo.level === 1) {
      return "";
    }
    return processOssImage(
      `https://s.xiaopeng.com/xp-charge/mini/chargeVip/level-up-popup-v${levelInfo?.level}.png`,
    );
  }, [levelInfo?.level]);

  useEffect(() => {
    // 监听页面数据变化
    if (configPageQuery?.data?.pageTitle) {
      document.title = configPageQuery?.data?.pageTitle;
    }
  }, [configPageQuery?.data?.pageTitle]);

  useMount(async () => {
    document.title = "充电会员";
    onWebviewClose(setNativeResult);
    navHide(false); // 小鹏APP内webview全屏

    await Promise.all([configPageQuery?.refetch]);

    Sentry.captureMessage("Page Ready", {
      tags: { page: "home" },
    });
  });

  useEffect(() => {
    if (goodsData) {
      console.log("goodsData", goodsData);
      const skuId = getQueryHashString("skuId") || "";
      const goods = goodsData?.[skuId] || Object.values(goodsData)[0];
      setDiscountCouponImg(goods?.actDiscountImgUrl || "");
    }
  }, [goodsData]);

  useEffect(() => {
    if (isLoading || !goodsData) {
      return;
    }

    const uid = userInfo?.uid;
    const today = new Date().toISOString().split("T")[0];
    console.log("today", today);

    const levelKey = `${uid}_vip_level`;
    const couponPopupKey = `${uid}_${today}_coupon_popup`;

    const skuId = getQueryHashString("skuId") || "";
    const goods = goodsData?.[skuId] || Object.values(goodsData)[0];
    const discountCouponImg = goods?.actDiscountImgUrl || "";

    const level = Number(localStorage.getItem(levelKey));
    const hasCouponPopup = localStorage.getItem(couponPopupKey);

    if (
      !!level &&
      levelInfo?.level &&
      Number(level) < levelInfo?.level &&
      levelInfo?.level > 1
    ) {
      setShowVipUpPopup(true);
      trackGdp("P11222", levelInfo?.level === 2 ? "B023" : "B024", {
        vip_level: levelInfo?.level ? `V${levelInfo?.level}` : "V0",
        userType,
        var_ch: entry(),
      });
    } else {
      if (discountCouponImg && !hasCouponPopup) {
        setShowDiscountPopup(true);
        localStorage.setItem(couponPopupKey, "true");
        // B040 活动折扣券图弹窗曝光
        trackGdp("P11222", "B040", {
          ...getReportVipData(levelInfo),
          page_id: configPageQuery?.data?.pageCode,
          versionId: configPageQuery?.data?.versionCode,
          activityId: goods?.actCode || "",
          uid,
          userType,
        });
      }
    }
    localStorage.setItem(levelKey, `${levelInfo?.level || 0}`);
  }, [
    goodsData,
    isLoading,
    levelInfo,
    userInfo?.uid,
    userType,
    configPageQuery?.data?.pageCode,
    configPageQuery?.data?.versionCode,
  ]);

  const setNativeResult = () => {
    callbackData({
      vipReturn: true,
    });
  };

  // 向Page配置页追加自定义组件渲染函数
  const customCompRender = useCallback((comp, originCompList) => {
    if (
      ["buyMemberGoods", "buyMemberCard", "customizeBuyMember"].includes(
        comp?.moduleType,
      ) &&
      comp?.moduleJsonData?.goodsInfo?.group?.length
    ) {
      const type = getGoodsType(
        comp?.moduleJsonData?.visualStyle?.componentStyle,
      );

      // originCompList中第一个type为ONE_ROW_X_GOODS的组件才展示底部BuyGoodContent
      let isFirst = false;
      const buyGoodsComp = originCompList?.find(
        (item) =>
          getGoodsType(item?.moduleJsonData?.visualStyle?.componentStyle) ===
          "ONE_ROW_X_GOODS",
      );
      if (buyGoodsComp) {
        isFirst = comp?.moduleId === buyGoodsComp?.moduleId;
      }

      // 处理底部BuyGoodContent显示状态变化的回调函数
      const handleStickyBuyContentVisible = (visible: boolean) => {
        setHasStickyBuyContent(visible);
      };

      return (
        <LazyRender key={comp?.moduleId}>
          <GoodsFocusProvider key={comp?.moduleId}>
            <TabGoodsContent
              comp={comp}
              moduleJsonData={comp?.moduleJsonData}
              group={comp?.moduleJsonData?.goodsInfo?.group}
              type={type}
              isShowButtomBuyContent={isFirst} // 设置第一个TabGoodsContent为true
              onStickyBuyContentVisible={handleStickyBuyContentVisible} // 添加回调函数
            />
          </GoodsFocusProvider>
        </LazyRender>
      );
    }
    if (comp?.moduleType === "myMemberGoods") {
      console.log("render myMemberGoods (CouponRedemptionComponent)");
      return (
        <LazyRender key={comp?.moduleId}>
          <CouponRedemptionComponent comp={comp} />
        </LazyRender>
      );
    }
    // 预约充电桩组件
    if (comp?.moduleType === "memberBooking") {
      console.log("render bookPile");
      return (
        <LazyRender key={comp?.moduleId}>
          <BookPile comp={comp} />
        </LazyRender>
      );
    }
    return null;
  }, []);

  const onPageScroll = useCallback(() => {
    if (!isXPApp) {
      return;
    }
    const scrollTop = window.scrollY;
    if (scrollTop >= rem2px(300) && !navBgWhite) {
      setNavBgWhite(true);
    } else if (scrollTop < rem2px(300) && navBgWhite) {
      setNavBgWhite(false);
    }
  }, [navBgWhite]);

  const { run } = useThrottleFn(onPageScroll, { wait: 100 });
  useEventListener("scroll", run);

  return (
    <>
      <div className="">
        <OpenBar title="小鹏充电VIP" />
        <AppNavBar bgWhite={navBgWhite} />
        <SkeletonScreen visible={isLoading} />
        <div
          className={clsx("charge-vip-home", {
            "home-container": hasStickyBuyContent,
            "with-open-bar": !isXPApp && !isWXApp && !isMyApp && !isWX,
            "with-app-nav-bar": isXPApp,
          })}
          ref={userHeaderRef}
        >
          <UserHeader className={isXPApp ? "user-header-in-app" : ""} />
          <Page customCompRender={customCompRender} />
        </div>
        <FullScreenPopup
          show={showDiscountPopup}
          onClose={() => setShowDiscountPopup(false)}
          onClick={() => setShowDiscountPopup(false)}
          imgUrl={discountCouponImg}
        />
        <FullScreenPopup
          show={showVipUpPopup}
          onClose={() => setShowVipUpPopup(false)}
          onClick={() => setShowVipUpPopup(false)}
          imgUrl={vipPopupImgUrl}
        />
      </div>
    </>
  );
};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
    },
  },
});

const App = () => {
  const pageCode = getQueryHashString("pageCode") || "";
  const versionCode = getQueryHashString("versionCode") || "";
  // 正确的hook调用位置
  const query = useFetchConfigPageForVip({
    pageCode,
    versionCode,
  });
  useMount(() => {
    Sentry.captureMessage("Page mounted", {
      tags: { page: "home" },
      extra: {
        fullUrl: window.location.href,
      },
    });
  });
  const handleShareUrl = (shareUrl: string) => {
    return removeUrlParameters(shareUrl, ["pageCode"]);
  };

  return (
    <ConfigPageProvider
      configPageQuery={query}
      pageOrigin="VIP"
      handleShareUrl={handleShareUrl}
    >
      <UserProviderWithConfigPage>
        <ConfigPage />
      </UserProviderWithConfigPage>
    </ConfigPageProvider>
  );
};

const WrappedQueryApp = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  );
};
export default WrappedQueryApp;
